/**
 * The cards of My Accounts' sections (TMMAPP-0102, A3; the v2 cards, TMMAPP-0151 §D 11–12):
 * `AccountCard` for a cash, revolving or loan account, `HoldingCard` for an asset or a tradable
 * holding. Both fill `RealAccountCard`'s one shape; this file owns each kind's content, the kebab items
 * and the D1 extras (unmanaged / closed tags, equity, financed by, marked as of, the last import).
 *
 * - **Cash:** the bank balance in the **account's** currency (MM-D-63), a bar of allocated over the
 *   bank balance (both in the operating currency, as served), and "{allocated} in envelopes ·
 *   {unallocated} unallocated" in the operating currency, plus " · {CUR} → {OPCUR}" when foreign.
 * - **A card or line:** the statement balance (negative), "{allocated} published to envelopes".
 * - **A loan:** the balance (negative), a paid bar and "{pct}% paid · {payment}/mo at {apr} · matures
 *   {date}"; `pct` is original minus balance over original (the page reads no schedule).
 * - **Assets:** "Cost {cost} · {±rate} a year · since {date}"; **investments:** "{±gain} since {date} ·
 *   {quantity} units", with "Type · Symbol · held at" as the meta. Holdings are "Only you".
 *
 * TMMAPP-0120: an owner's card in the reorder set carries the corner handle and Move earlier / Move
 * later in its kebab (pattern 15); a bank-linked account its connection status.
 *
 * TMMAPP-0159 §F 33: an own account with something to review shows "{n} to review", a warning-tone
 * chip that opens the account on its Items to review tab (`?tab=review`). The count is the served
 * `byAccount` total of `GET /v1/me/review/summary`, never derived.
 */
import type { MouseEvent, ReactNode } from 'react';
import { Button, Link, ListSubheader, MenuItem, Typography, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { institutionLabel, type Account, type InstitutionProposal } from '@/api/accounts';
import type { Holding } from '@/api/holdings';
import { MoneyParts, MoneyText } from '@/components/MoneyText';
import { Pill } from '@/components/Pill';
import type { ProgressTone } from '@/components/ProgressTrack';
import { ReorderHandle, type ReorderHandleProps } from '@/components/SortableCardGrid';
import { statusLabel } from '@/components/statusLabel';
import {
  formatCivilDate,
  formatCivilDateNear,
  formatMoneyMinor,
  formatNumber,
  formatPercent,
} from '@/i18n/format';
import { todayCivilInZone, utcToZonedCivil } from '@/i18n/zonedCivil';
import { useLocale } from '@/i18n/useLocale';
import { destructiveItemSx } from '@/shell/objectMenu';
import { resolveTokens } from '@/theme/theme';
import { isForeignCurrency, isOwnerAccount } from './accountAccess';
import { accountPermissionChip, holdingPermissionChip } from './permissionChip';
import { CardMenu, RealAccountCard } from './RealAccountCard';
import { useInstitutionAnswer } from './useInstitutionAnswer';
import { SubscriberActionHint } from '@/session/useSubscriberAction';

/** What the kebab offers; an absent action is not rendered, and no actions means no kebab. */
export interface AccountCardActions {
  edit?: () => void;
  permissions?: () => void;
  import?: { run: () => void; disabled: boolean; label: string };
  /**
   * The starting balance (TMMAPP-0123 item 16): a pending account shows "Set starting balance" on the
   * card; a set one offers "Edit starting balance" in the kebab. Both open the edit form there.
   */
  opening?: { run: () => void; pending: boolean };
  close?: () => void;
  /**
   * A standing institution proposal (TMMAPP-0163 item 12): "Is this {name}?" with Yes and No in the
   * kebab. Each answer re-reads the page.
   */
  institution?: {
    account: Account;
    proposal: InstitutionProposal;
    onAnswered: () => void;
    onNotify: (message: string) => void;
  };
}

export interface HoldingCardActions {
  edit?: () => void;
  value?: () => void;
  dispose?: () => void;
}

const MASK = '····';

/** "{institution} ···· {last4} · {subtype}" (P:470), whatever of it exists. */
function accountMeta(account: Account, t: TFunction): string {
  const identity = [
    institutionLabel(account) ?? '',
    account.last4 !== null && account.last4 !== '' ? `${MASK} ${account.last4}` : '',
  ]
    .filter((part) => part.length > 0)
    .join(' ');
  return [identity, t(`accounts.subtype.${account.subtype}`)]
    .filter((part) => part.length > 0)
    .join(' · ');
}

/**
 * The last import's date: an instant converted to the viewer's zone (a civil date as received), with
 * this year's dates leaving the year out (TMMAPP-0156 item 2).
 */
function whenText(value: string, locale: string, timeZone: string): string {
  try {
    const today = todayCivilInZone(timeZone);
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return formatCivilDateNear(value, locale, today);
    return formatCivilDateNear(utcToZonedCivil(value, timeZone).date, locale, today);
  } catch {
    return value;
  }
}

/** The card's balance in `figure`: `link` (the prototype's `brand`), `moneyNegative` below zero. */
export function CardFigure({ minor, currency }: { minor: number | null; currency: string | null }) {
  const tokens = resolveTokens(useTheme());
  const negative = minor !== null && minor < 0;
  return (
    <Typography
      variant="figure"
      component="div"
      sx={{
        color: negative ? tokens.moneyNegative : tokens.link,
        fontVariantNumeric: 'tabular-nums',
        // Never cut a figure short: a long one wraps rather than hides.
        overflowWrap: 'anywhere',
      }}
    >
      {minor === null || currency === null || currency === '' ? (
        '—'
      ) : (
        <MoneyParts minor={minor} currency={currency} />
      )}
    </Typography>
  );
}

interface AccountCardProps {
  account: Account;
  operating: string | null;
  timeZone: string;
  actions: AccountCardActions;
  /** The grid's reorder wiring; null outside the reorder set and in the drag overlay. */
  reorder?: ReorderHandleProps | null | undefined;
  /** The bank connection's status, for a bank-linked account. */
  status?: ReactNode;
  /** The account's own served review count (`byAccount`); absent or 0 shows no chip. */
  reviewCount?: number | undefined;
}

/**
 * "{n} to review": the warning pill, a link to the account's Items to review tab. Its name carries
 * the account ("Checking: 2 to review"), so a list of links tells the cards apart.
 */
function ReviewChip({
  accountId,
  accountName,
  count,
}: {
  accountId: number;
  accountName: string;
  count: number;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  return (
    <Link
      component={RouterLink}
      to={`/me/accounts/${accountId}?tab=review`}
      underline="none"
      aria-label={t('accounts.reviewChip', { account: accountName, count })}
      data-review-chip=""
      sx={{
        display: 'inline-flex',
        borderRadius: '11px',
        '&:hover span': { textDecoration: 'underline' },
        '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
      }}
    >
      <Pill tone="warning">{t('shell.reviewCount', { count })}</Pill>
    </Link>
  );
}

/** Move earlier / Move later, the drag's fallback in the owner's kebab (pattern 15). */
function moveItems(
  reorder: ReorderHandleProps | null | undefined,
  close: () => void,
  t: TFunction,
) {
  if (reorder === null || reorder === undefined) return [];
  return [
    <MenuItem
      key="earlier"
      disabled={!reorder.canMoveEarlier}
      onClick={() => {
        close();
        reorder.onMoveEarlier();
      }}
    >
      {t('reorder.moveEarlier')}
    </MenuItem>,
    <MenuItem
      key="later"
      disabled={!reorder.canMoveLater}
      onClick={() => {
        close();
        reorder.onMoveLater();
      }}
    >
      {t('reorder.moveLater')}
    </MenuItem>,
  ];
}

/** Basis points as a signed percent ("+4.2%", "−14%"), one decimal at most. */
function signedPercent(bp: number, locale: string): string {
  return formatPercent(bp / 10000, locale, { maximumFractionDigits: 1, signDisplay: 'exceptZero' });
}

/** A share of a whole, 0–1, or null when either is missing or the whole isn't positive. */
function share(part: number | null | undefined, whole: number | null | undefined): number | null {
  if (part === null || part === undefined || whole === null || whole === undefined) return null;
  if (whole <= 0) return null;
  return Math.max(0, Math.min(1, part / whole));
}

export function AccountCard({
  account,
  operating,
  timeZone,
  actions,
  reorder,
  status,
  reviewCount,
}: AccountCardProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const owner = isOwnerAccount(account);
  const unmanaged = account.management === 'UNMANAGED';
  const closed = account.status === 'CLOSED';
  const currency = account.currency ?? operating;
  const foreign = isForeignCurrency(account);
  const balances = account.balances;
  const money = (minor: number) => formatMoneyMinor(minor, operating, locale);

  const tags = [
    ...(closed ? [statusLabel(t, 'account', 'CLOSED')] : []),
    ...(unmanaged ? [t('accounts.unmanagedChip')] : []),
  ];

  let balanceMinor: number | null;
  let bar: number | null = null;
  let barLabel: string | undefined;
  let barTone: ProgressTone | undefined;
  let line: ReactNode = null;
  let footer: ReactNode = null;

  if (account.type === 'CASH') {
    balanceMinor = balances?.bankBalanceMinor ?? null;
    const allocated = balances?.allocatedMinor ?? null;
    const unallocated = balances?.unallocatedMinor ?? null;
    bar = share(allocated, balances?.operatingBalanceMinor);
    barLabel = t('accounts.cardAllocatedBar');
    if (allocated !== null && unallocated !== null) {
      const text = t('accounts.cardCashLine', {
        allocated: money(allocated),
        unallocated: money(unallocated),
      });
      line = foreign
        ? t('accounts.cardCashLineForeign', {
            line: text,
            from: currency ?? '',
            to: operating ?? '',
          })
        : text;
    }
  } else if (account.type === 'REVOLVING') {
    const statement = balances?.statement ?? null;
    balanceMinor = statement === null ? null : 0 - Math.abs(statement);
    const published = balances?.allocatedMinor ?? null;
    if (published !== null) {
      line = t('accounts.cardPublishedLine', { amount: money(Math.abs(published)) });
    }
  } else {
    // A pending opening nulls every balance figure (contract): no amortized fallback stands in.
    const fromRegister = balances?.balance != null;
    const current = account.openingPending
      ? null
      : fromRegister
        ? balances!.balance
        : (balances?.amortized ?? null);
    balanceMinor = current === null ? null : 0 - Math.abs(current);
    const original = account.loan?.originalMinor ?? null;
    const paid =
      current === null || original === null ? null : share(original - Math.abs(current), original);
    bar = paid;
    barLabel = t('accounts.cardPaidBar');
    barTone = 'loan';
    const apr = account.aprBp ?? balances?.aprBp ?? null;
    const matures = account.loan?.maturesOn ?? balances?.maturesOn ?? null;
    const payment = account.loan?.paymentMinor ?? null;
    const parts = [
      paid === null
        ? null
        : t('accounts.cardPaid', {
            pct: formatPercent(paid, locale, { maximumFractionDigits: 0 }),
          }),
      payment === null
        ? null
        : apr === null
          ? t('accounts.cardPayment', { payment: formatMoneyMinor(payment, currency, locale) })
          : t('accounts.cardPaymentApr', {
              payment: formatMoneyMinor(payment, currency, locale),
              apr: formatPercent(apr / 10000, locale, { maximumFractionDigits: 3 }),
            }),
      matures === null
        ? null
        : t('accounts.cardMatures', { date: formatCivilDate(matures, locale) }),
    ].filter((part): part is string => part !== null);
    line = parts.length > 0 ? parts.join(' · ') : null;
  }

  if (account.type !== 'LOAN') {
    const last = account.lastImport;
    if (last !== null && last.at !== null) {
      const when = whenText(last.at, locale, timeZone);
      footer =
        last.profileName !== null && last.profileName !== ''
          ? t('accounts.cardLastImport', { when, profile: last.profileName })
          : t('accounts.cardLastImportBare', { when });
    } else if (owner && !unmanaged) {
      footer = t('accounts.cardNoImport');
    }
  }

  const title = [account.name, account.last4 ? `${MASK} ${account.last4}` : '']
    .filter((part) => part.length > 0)
    .join(' ');
  const openingPending = actions.opening?.pending === true;
  const hasMenu =
    actions.edit !== undefined ||
    (actions.opening !== undefined && !openingPending) ||
    actions.permissions !== undefined ||
    actions.import !== undefined ||
    actions.close !== undefined ||
    actions.institution !== undefined ||
    (reorder !== null && reorder !== undefined);

  return (
    <RealAccountCard
      name={account.name}
      to={`/me/accounts/${account.id}`}
      chip={accountPermissionChip(account, t)}
      meta={accountMeta(account, t)}
      tags={tags}
      balance={<CardFigure minor={balanceMinor} currency={currency} />}
      bar={bar}
      barLabel={barLabel}
      barTone={barTone}
      line={line}
      footer={footer}
      {...(openingPending
        ? {
            extras: (
              <Button
                variant="text"
                size="small"
                onClick={actions.opening?.run}
                sx={{ alignSelf: 'flex-start', marginInlineStart: '-5px' }}
              >
                {t('accounts.setOpening')}
              </Button>
            ),
          }
        : {})}
      dim={closed}
      handle={reorder ? <ReorderHandle {...reorder} corner="end" /> : undefined}
      status={status}
      review={
        reviewCount !== undefined && reviewCount > 0 ? (
          <ReviewChip accountId={account.id} accountName={account.name} count={reviewCount} />
        ) : undefined
      }
      {...(hasMenu
        ? {
            menu: (
              <CardMenu title={title} label={t('accounts.actionsFor', { name: account.name })}>
                {(close) => [
                  actions.edit && (
                    <MenuItem
                      key="edit"
                      onClick={() => {
                        close();
                        actions.edit?.();
                      }}
                    >
                      {t('accounts.edit')}
                    </MenuItem>
                  ),
                  actions.opening && !openingPending && (
                    <MenuItem
                      key="opening"
                      onClick={() => {
                        close();
                        actions.opening?.run();
                      }}
                    >
                      {t('accounts.editOpening')}
                    </MenuItem>
                  ),
                  actions.permissions && (
                    <MenuItem
                      key="permissions"
                      onClick={() => {
                        close();
                        actions.permissions?.();
                      }}
                    >
                      {t('accounts.tabPermissions')}
                    </MenuItem>
                  ),
                  actions.import && (
                    <MenuItem
                      key="import"
                      disabled={actions.import.disabled}
                      onClick={() => {
                        close();
                        actions.import?.run();
                      }}
                    >
                      {actions.import.label}
                    </MenuItem>
                  ),
                  ...institutionItems(actions.institution, close),
                  ...moveItems(reorder, close, t),
                  actions.close && <CloseItem key="close" onSelect={actions.close} close={close} />,
                ]}
              </CardMenu>
            ),
          }
        : {})}
    />
  );
}

type InstitutionAction = NonNullable<AccountCardActions['institution']>;

/**
 * "Is this {name}?" (a quiet line, hidden from the menu's reading order) and its Yes and No items,
 * each named for the bank (TMMAPP-0163 item 12).
 */
function institutionItems(action: InstitutionAction | undefined, close: () => void) {
  if (action === undefined) return [];
  return [
    <InstitutionQuestion key="institution-question" name={action.proposal.name} />,
    <InstitutionAnswerItem key="institution-yes" action={action} yes close={close} />,
    <InstitutionAnswerItem key="institution-no" action={action} yes={false} close={close} />,
  ];
}

function InstitutionQuestion({ name }: { name: string }) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  return (
    // A subheader, so the menu never gives it first focus (TMMAPP-0165 item 18).
    <ListSubheader
      aria-hidden
      disableSticky
      sx={{
        p: '6px 16px 2px',
        typography: 'caption',
        lineHeight: 'inherit',
        bgcolor: 'transparent',
        color: tokens.textLabel('surfaceRaised'),
        pointerEvents: 'none',
      }}
    >
      {t('institutions.proposal.question', { name })}
    </ListSubheader>
  );
}

function InstitutionAnswerItem({
  action,
  yes,
  close,
}: {
  action: InstitutionAction;
  yes: boolean;
  close: () => void;
}) {
  const { t } = useTranslation();
  const answer = useInstitutionAnswer(action.account, action.proposal, action);
  const run = yes ? answer.yes : answer.no;
  const name = action.proposal.name;
  return (
    <MenuItem
      aria-describedby={run.descriptionId}
      onClick={(event) => {
        close();
        run.run(event as unknown as MouseEvent<HTMLButtonElement>);
      }}
    >
      {t(yes ? 'institutions.proposal.yesName' : 'institutions.proposal.noName', { name })}
      <SubscriberActionHint id={run.descriptionId} description={run.description} />
    </MenuItem>
  );
}

function CloseItem({ onSelect, close }: { onSelect: () => void; close: () => void }) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  return (
    <MenuItem
      sx={destructiveItemSx(tokens)}
      onClick={() => {
        close();
        onSelect();
      }}
    >
      {t('accounts.close')}
    </MenuItem>
  );
}

interface HoldingCardProps {
  holding: Holding;
  accounts: Account[];
  currency: string | null;
  actions: HoldingCardActions;
  /** The grid's reorder wiring; null outside the reorder set and in the drag overlay. */
  reorder?: ReorderHandleProps | null | undefined;
}

export function HoldingCard({ holding, accounts, currency, actions, reorder }: HoldingCardProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const disposed = holding.status === 'DISPOSED';
  const tradable = holding.type === 'TRADABLE';
  const loan =
    holding.financedByAccountId === null
      ? null
      : (accounts.find((account) => account.id === holding.financedByAccountId) ?? null);
  const since = holding.acquiredOn !== null ? formatCivilDate(holding.acquiredOn, locale) : null;
  const subtype = t(`holdings.subtype.${holding.subtype}`);

  let valueMinor: number | null;
  let meta: string;
  let line: string | null;
  const extras: ReactNode[] = [];

  if (tradable) {
    valueMinor = holding.currentValueMinor;
    meta = [subtype, holding.symbol ?? '', holding.heldAt ?? '']
      .filter((part) => part.length > 0)
      .join(' · ');
    const parts = [
      holding.gainBp === null
        ? null
        : since === null
          ? t('holdings.cardGainBare', { gain: signedPercent(holding.gainBp, locale) })
          : t('holdings.cardGain', { gain: signedPercent(holding.gainBp, locale), date: since }),
      holding.quantity === null
        ? null
        : t('holdings.cardUnits', {
            units: formatNumber(holding.quantity, locale, { maximumFractionDigits: 8 }),
          }),
    ].filter((part): part is string => part !== null);
    line = parts.length > 0 ? parts.join(' · ') : null;
    if (holding.currentPriceAsOf !== null) {
      extras.push(
        <Typography key="marked" variant="caption" sx={{ color: tokens.textLabel('surface') }}>
          {t('holdings.cardMarkedAsOf', {
            date: formatCivilDate(holding.currentPriceAsOf, locale),
          })}
          {holding.priceSource === 'API' ? ` · ${t('holdings.priceApi')}` : ''}
        </Typography>,
      );
    }
  } else {
    valueMinor = holding.estimatedValueMinor;
    meta = [subtype, holding.description ?? ''].filter((part) => part.length > 0).join(' · ');
    const parts = [
      holding.acquisitionCostMinor === null
        ? null
        : t('holdings.cardCost', {
            cost: formatMoneyMinor(holding.acquisitionCostMinor, currency, locale),
          }),
      holding.annualRateBp === null
        ? null
        : t('holdings.cardRate', { rate: signedPercent(holding.annualRateBp, locale) }),
      since === null ? null : t('holdings.cardSince', { date: since }),
    ].filter((part): part is string => part !== null);
    line = parts.length > 0 ? parts.join(' · ') : null;
    if (holding.equityMinor !== null) {
      extras.push(
        <Typography key="equity" variant="caption" sx={{ color: tokens.textLabel('surface') }}>
          {t('holdings.colEquity')}{' '}
          <MoneyText minor={holding.equityMinor} currency={currency} variant="caption" />
        </Typography>,
      );
    }
    if (loan !== null) {
      extras.push(
        <Link
          key="financed"
          component={RouterLink}
          to={`/me/accounts/${loan.id}`}
          underline="hover"
          variant="caption"
          sx={{ color: tokens.text2, alignSelf: 'flex-start' }}
        >
          {t('holdings.financedBy', { name: loan.name })}
        </Link>,
      );
    }
  }

  const hasMenu =
    actions.edit !== undefined ||
    actions.value !== undefined ||
    actions.dispose !== undefined ||
    (reorder !== null && reorder !== undefined);
  const tags = disposed
    ? [
        statusLabel(t, 'holding', 'DISPOSED'),
        ...(holding.disposedOn !== null ? [formatCivilDate(holding.disposedOn, locale)] : []),
      ]
    : [];

  return (
    <RealAccountCard
      name={holding.name}
      to={`/me/holdings/${holding.id}`}
      chip={holdingPermissionChip(t)}
      meta={meta}
      tags={tags}
      balance={<CardFigure minor={valueMinor} currency={currency} />}
      line={line}
      extras={extras.length > 0 ? extras : null}
      dim={disposed}
      handle={reorder ? <ReorderHandle {...reorder} corner="end" /> : undefined}
      {...(hasMenu
        ? {
            menu: (
              <CardMenu
                title={holding.name}
                label={t('accounts.actionsFor', { name: holding.name })}
              >
                {(close) => [
                  actions.edit && (
                    <MenuItem
                      key="edit"
                      onClick={() => {
                        close();
                        actions.edit?.();
                      }}
                    >
                      {t('holdings.edit')}
                    </MenuItem>
                  ),
                  actions.value && (
                    <MenuItem
                      key="value"
                      onClick={() => {
                        close();
                        actions.value?.();
                      }}
                    >
                      {tradable ? t('holdings.markPrice') : t('holdings.value')}
                    </MenuItem>
                  ),
                  ...moveItems(reorder, close, t),
                  actions.dispose && (
                    <MenuItem
                      key="dispose"
                      sx={destructiveItemSx(tokens)}
                      onClick={() => {
                        close();
                        actions.dispose?.();
                      }}
                    >
                      {t('holdings.dispose')}
                    </MenuItem>
                  ),
                ]}
              </CardMenu>
            ),
          }
        : {})}
    />
  );
}
